/**
 * Checkbox-card group for selecting delegation permissions. Shared by the
 * admin invite form, the owner invite dialog and both edit-permissions
 * flows.
 */

import {
  DELEGATION_PERMISSIONS,
  PERMISSION_DESCRIPTIONS,
  PERMISSION_LABELS,
  type DelegationPermission,
} from './types';

type PermissionToggleGroupProps = {
  value: DelegationPermission[];
  onChange: (next: DelegationPermission[]) => void;
  disabled?: boolean;
  /** Differentiates input ids when several groups are mounted at once. */
  idPrefix?: string;
};

export function PermissionToggleGroup({
  value,
  onChange,
  disabled = false,
  idPrefix = 'permission-toggle',
}: PermissionToggleGroupProps) {
  const toggle = (permission: DelegationPermission) => {
    onChange(
      value.includes(permission)
        ? value.filter((p) => p !== permission)
        : [...value, permission],
    );
  };

  return (
    <fieldset
      className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      disabled={disabled}
      data-testid="permission-toggle-group"
    >
      <legend className="sr-only">Workspace permissions</legend>
      {DELEGATION_PERMISSIONS.map((permission) => {
        const checked = value.includes(permission);
        const inputId = `${idPrefix}-${permission}`;
        // Input is a SIBLING of the label (not nested) so a click never
        // double-fires through the label's implicit activation behavior.
        return (
          <div
            key={permission}
            className={`relative flex items-start gap-2.5 rounded-lg border p-3 transition-colors ${
              checked
                ? 'border-[#026857] bg-[#026857]/5'
                : 'border-gray-200 bg-white hover:border-gray-300'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <input
              id={inputId}
              type="checkbox"
              checked={checked}
              onChange={() => toggle(permission)}
              className="mt-0.5 rounded border-gray-300 text-[#026857] focus:ring-[#026857]"
              aria-label={PERMISSION_LABELS[permission]}
            />
            <label
              htmlFor={inputId}
              className={`min-w-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} after:absolute after:inset-0`}
            >
              <span className="block text-sm font-medium text-gray-900">
                {PERMISSION_LABELS[permission]}
              </span>
              <span className="block text-xs text-gray-500">
                {PERMISSION_DESCRIPTIONS[permission]}
              </span>
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}
